import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from '../../../../../components/slides/highlighted/common/fontAndBackgroundColorPicker';
import SectionTitle from '../../../../../components/elements/common/SectionTitle';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';
import {ButtonTheme, FontSize, FormThemeKey, FormTitleTheme, IPresentationTheme} from '../../../../../incase-domain/presentationTheme';
import {formElementTypeToThemeKey} from '../../../../../utils/presentationTheme';
import {buildThemeControlElement} from '../buildThemePreviewElementByType';
import SelectQuestionElementFontSize from '../questionElement/SelectQuestionElementFontSize';
import {patchThemeStyles, toThemeFontSize} from '../themeStyle';

interface PresentationThemeFormElementProps {
    presentationTheme: IPresentationTheme;
    setPresentationTheme: React.Dispatch<React.SetStateAction<IPresentationTheme>>;
    formType: ELEMENT;
}

export default function PresentationThemeFormElement(props: PresentationThemeFormElementProps): JSX.Element {
    const {presentationTheme, setPresentationTheme, formType} = props;
    const {t} = useTranslation();

    const themeKey: FormThemeKey = formElementTypeToThemeKey[formType] ?? 'buttonElement';
    const isButton = themeKey === 'buttonElement';

    const buttonTheme = presentationTheme.elementStyles?.buttonElement;
    const titleTheme: FormTitleTheme | undefined =
        themeKey === 'textInputElement' ? presentationTheme.elementStyles?.textInputElement : presentationTheme.elementStyles?.formElement;

    const mockButton = useMemo(
        () => (isButton ? buildThemeControlElement(ELEMENT.BUTTON, presentationTheme) : null),
        [isButton, presentationTheme]
    );

    const setButtonPatch = (patch: Partial<ButtonTheme>): void => {
        setPresentationTheme((prevTheme) => patchThemeStyles(prevTheme, ['buttonElement'], patch));
    };

    const setTitleFontSize = (fontSize: FontSize | undefined): void => {
        setPresentationTheme((prevTheme) =>
            patchThemeStyles(prevTheme, [themeKey], {fontSize: toThemeFontSize(fontSize, prevTheme.fontSizes)})
        );
    };

    const changeHandler = {
        single: (field: string, value: unknown): void => {
            if (field === 'font' && value && typeof value === 'object' && 'color' in value) {
                setButtonPatch({color: (value as {color: string}).color});
            } else if (field === 'color' && typeof value === 'string') {
                setButtonPatch({backgroundColor: value});
            }
        },
    };

    return (
        <div className="l-group--form">
            <SectionTitle label={t('designSidepanel.style')} />
            <div className="u-flex u-flex-col u-gap-3">
                {isButton && mockButton ? (
                    <>
                        <SelectQuestionElementFontSize
                            title={t('presentationThemesPage.text.textElementSize')}
                            placeholder={t('presentationThemesPage.text.select')}
                            elementFontSize={buttonTheme?.fontSize}
                            setElementFontSize={(fontSize) =>
                                setButtonPatch(fontSize ? {fontSize: toThemeFontSize(fontSize, presentationTheme.fontSizes)} : {})
                            }
                            presentationTheme={presentationTheme}
                        />
                        <FontAndBackgroundColorPicker
                            highlightedElement={mockButton}
                            highlightedElementChangeHandler={changeHandler}
                            colorFields={[COLOR_FIELDS.FONT_COLOR, COLOR_FIELDS.BACKGROUND_COLOR]}
                        />
                    </>
                ) : (
                    <SelectQuestionElementFontSize
                        title={t('presentationThemesPage.text.textElementSize')}
                        placeholder={t('presentationThemesPage.text.select')}
                        elementFontSize={titleTheme?.fontSize}
                        setElementFontSize={setTitleFontSize}
                        presentationTheme={presentationTheme}
                    />
                )}
            </div>
        </div>
    );
}
